import React, { useState } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { motion, AnimatePresence } from 'framer-motion';

// PayPal Client ID
const PAYPAL_CLIENT_ID = 'AQIXmMiMNOHhqb9q19cglRVvyd-Ysy60I2gLxlD4B6bgauZm7VE_lpleY3ZMLW-hVvrayolTiKKmm03e';

// PayPal Checkout Modal Component
export const PayPalCheckoutModal = ({ isOpen, onClose, plan, annual }) => {
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const price = annual ? plan.annualPrice : plan.monthlyPrice;
  const billingCycle = annual ? 'year' : 'month';
  const totalPrice = annual ? price * 12 : price;
  const trialDiscount = totalPrice; // 14-day free trial covers first billing period
  const dueToday = Math.max(0, totalPrice - trialDiscount);

  const handleApprove = (data, actions) => {
    setIsProcessing(true);
    return actions.order.capture().then((details) => {
      setPaymentStatus('success');
      setIsProcessing(false);
      console.log('Payment completed:', details);
      // Here you would typically:
      // 1. Send details to your backend
      // 2. Create user account / activate subscription
      // 3. Redirect to dashboard
    });
  };

  const handleError = (err) => {
    console.error('PayPal Error:', err);
    setPaymentStatus('error');
    setIsProcessing(false);
  };

  const handleCancel = () => {
    setPaymentStatus('cancelled');
  };

  const resetAndClose = () => {
    setPaymentStatus(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <PayPalScriptProvider options={{ 
      clientId: PAYPAL_CLIENT_ID,
      currency: 'USD',
      intent: 'capture',
    }}>
      <AnimatePresence>
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={resetAndClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal Content */}
          <motion.div
            className="relative bg-dark-deeper border border-neon-orange/20 rounded-2xl max-w-md w-full overflow-hidden"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            style={{
              boxShadow: '0 0 60px rgba(253, 128, 46, 0.15)'
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-neon-orange/10">
              <div>
                <h3 className="text-xl font-heading font-bold text-white">
                  {plan.name} Plan
                </h3>
                <p className="text-gray-400 text-sm mt-1">14-day free trial included</p>
              </div>
              <button
                onClick={resetAndClose}
                className="w-10 h-10 rounded-full bg-dark-base flex items-center justify-center text-gray-400 hover:text-white hover:bg-neon-orange/20 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Payment Status Messages */}
              {paymentStatus === 'success' && (
                <motion.div
                  className="text-center py-8"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-white mb-2">Payment Successful!</h4>
                  <p className="text-gray-400 mb-6">Your {plan.name} plan is now active.</p>
                  <motion.button
                    onClick={resetAndClose}
                    className="px-6 py-3 bg-neon-orange text-dark-base font-bold rounded-full"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Get Started
                  </motion.button>
                </motion.div>
              )}

              {paymentStatus === 'error' && (
                <motion.div
                  className="text-center py-8"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-white mb-2">Payment Failed</h4>
                  <p className="text-gray-400 mb-6">Something went wrong. Please try again.</p>
                  <motion.button
                    onClick={() => setPaymentStatus(null)}
                    className="px-6 py-3 bg-neon-orange text-dark-base font-bold rounded-full"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Try Again
                  </motion.button>
                </motion.div>
              )}

              {paymentStatus === 'cancelled' && (
                <motion.div
                  className="text-center py-8"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-white mb-2">Payment Cancelled</h4>
                  <p className="text-gray-400 mb-6">Your payment was not completed.</p>
                  <motion.button
                    onClick={() => setPaymentStatus(null)}
                    className="px-6 py-3 bg-neon-orange text-dark-base font-bold rounded-full"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Try Again
                  </motion.button>
                </motion.div>
              )}

              {/* Default Checkout View */}
              {!paymentStatus && (
                <>
                  {/* Order Summary */}
                  <div className="bg-dark-base rounded-xl p-4 mb-6">
                    <h4 className="text-white font-semibold mb-3">Order Summary</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between text-gray-400">
                        <span>{plan.name} Plan ({annual ? 'Annual' : 'Monthly'})</span>
                        <span>${price}/mo</span>
                      </div>
                      {annual && (
                        <div className="flex justify-between text-gray-400">
                          <span>Billed annually</span>
                          <span>${totalPrice}/yr</span>
                        </div>
                      )}
                      <div className="flex justify-between text-neon-orange">
                        <span>14-day free trial</span>
                        <span>-${annual ? totalPrice : price}</span>
                      </div>
                      <div className="border-t border-gray-700 pt-2 mt-2">
                        <div className="flex justify-between text-white font-semibold">
                          <span>Due today</span>
                          <span>$0.00</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Features Reminder */}
                  <div className="mb-6">
                    <h4 className="text-gray-400 text-sm mb-2">What you'll get:</h4>
                    <ul className="space-y-1">
                      {plan.features.slice(0, 3).map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                          <svg className="w-4 h-4 text-neon-orange" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                          </svg>
                          {feature}
                        </li>
                      ))}
                      {plan.features.length > 3 && (
                        <li className="text-sm text-neon-orange">+ {plan.features.length - 3} more features</li>
                      )}
                    </ul>
                  </div>

                  {/* PayPal Buttons / Trial CTA */}
                  <div className="paypal-button-container">
                    {isProcessing && (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neon-orange"></div>
                        <span className="ml-3 text-gray-400">Processing payment...</span>
                      </div>
                    )}

                    {!isProcessing && dueToday === 0 && (
                      <motion.button
                        onClick={() => {
                          setIsProcessing(true);
                          // Simulate activation of free trial without payment
                          setTimeout(() => {
                            setPaymentStatus('success');
                            setIsProcessing(false);
                          }, 700);
                        }}
                        className="w-full py-3 rounded-full font-bold bg-neon-orange text-white"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Get Started
                      </motion.button>
                    )}

                    {!isProcessing && dueToday !== 0 && (
                      <PayPalButtons
                        style={{
                          layout: 'vertical',
                          color: 'gold',
                          shape: 'rect',
                          label: 'subscribe',
                          height: 45,
                        }}
                        createOrder={(data, actions) => {
                          return actions.order.create({
                            purchase_units: [
                              {
                                description: `${plan.name} Plan - ${annual ? 'Annual' : 'Monthly'} Subscription (14-day free trial)`,
                                amount: {
                                  currency_code: 'USD',
                                  value: '0.00', // Free trial - $0 today
                                },
                                custom_id: JSON.stringify({
                                  plan: plan.name.toLowerCase(),
                                  billing: annual ? 'annual' : 'monthly',
                                  price: price,
                                  trial: true,
                                }),
                              },
                            ],
                            application_context: {
                              shipping_preference: 'NO_SHIPPING',
                            },
                          });
                        }}
                        onApprove={handleApprove}
                        onError={handleError}
                        onCancel={handleCancel}
                      />
                    )}
                  </div>

                  {/* Security Note */}
                  <div className="flex items-center justify-center gap-2 mt-4 text-gray-500 text-xs">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <span>Secure payment powered by PayPal</span>
                  </div>

                  {/* Terms */}
                  <p className="text-center text-gray-500 text-xs mt-4">
                    By subscribing, you agree to our{' '}
                    <a href="/terms" className="text-neon-orange hover:underline">Terms of Service</a>
                    {' '}and{' '}
                    <a href="/privacy" className="text-neon-orange hover:underline">Privacy Policy</a>.
                    <br />
                    Cancel anytime during your trial.
                  </p>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </PayPalScriptProvider>
  );
};

export default PayPalCheckoutModal;
